import React, { useState } from 'react';
import { X, Gift, Heart, Star, DollarSign } from 'lucide-react';

const GiftModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    recipientName: '',
    recipientEmail: '',
    giftPackage: 'starter',
    senderName: '',
    senderEmail: '',
    personalMessage: '',
    deliveryDate: '',
    customAmount: ''
  });

  const giftPackages = [
    {
      id: 'starter',
      name: 'Starter Package',
      price: '$149',
      sessions: '3 Personal Training Sessions',
      bonus: 'Nutrition Guide',
      popular: false
    },
    {
      id: 'premium',
      name: 'Premium Package',
      price: '$299',
      sessions: '6 Personal Training Sessions',
      bonus: 'Custom Meal Plan + Progress Tracking',
      popular: true
    },
    {
      id: 'ultimate',
      name: 'Ultimate Package',
      price: '$499',
      sessions: '10 Personal Training Sessions',
      bonus: 'Complete Transformation Program',
      popular: false
    },
    {
      id: 'cash',
      name: 'Cash Gift',
      price: 'Custom Amount',
      sessions: 'Flexible Training Sessions',
      bonus: 'Gift Card',
      popular: false,
      isCash: true
    }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Gift package form submitted:', formData);
    // Handle form submission here
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
          <div className="flex items-center space-x-3">
            <Gift className="text-white" size={32} />
            <div>
              <h2 className="text-2xl font-bold text-white">Gift a Transformation</h2>
              <p className="text-purple-100">Give the gift of health and fitness</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Gift Package Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Choose Gift Package
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {giftPackages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                    formData.giftPackage === pkg.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                  onClick={() => setFormData({ ...formData, giftPackage: pkg.id })}
                >
                  {pkg.popular && (
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                        <Star size={12} className="mr-1" />
                        POPULAR
                      </span>
                    </div>
                  )}
                  <input
                    type="radio"
                    name="giftPackage"
                    value={pkg.id}
                    checked={formData.giftPackage === pkg.id}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-gray-800">{pkg.name}</h3>
                    <p className="text-2xl font-bold text-purple-600 my-2">{pkg.price}</p>
                    <p className="text-sm text-gray-600 mb-2">{pkg.sessions}</p>
                    <p className="text-xs text-purple-600 font-medium">{pkg.bonus}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Amount Input for Cash Gift */}
          {formData.giftPackage === 'cash' && (
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Gift Amount *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="customAmount"
                  value={formData.customAmount}
                  onChange={handleInputChange}
                  required
                  min="1"
                  step="1"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  placeholder="Enter amount"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum amount: $1</p>
            </div>
          )}

          {/* Recipient Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Recipient Name *
              </label>
              <input
                type="text"
                name="recipientName"
                value={formData.recipientName}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="Who is this gift for?"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Recipient Email *
              </label>
              <input
                type="email"
                name="recipientEmail"
                value={formData.recipientEmail}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="their@email.com"
              />
            </div>
          </div>

          {/* Sender Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Name *
              </label>
              <input
                type="text"
                name="senderName"
                value={formData.senderName}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Email *
              </label>
              <input
                type="email"
                name="senderEmail"
                value={formData.senderEmail}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="your@email.com"
              />
            </div>
          </div>

          {/* Personal Message */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Personal Message
            </label>
            <textarea
              name="personalMessage"
              value={formData.personalMessage}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
              placeholder="Add a personal touch to your gift..."
            />
          </div>

          {/* Delivery Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Delivery Date (Optional)
            </label>
            <input
              type="date"
              name="deliveryDate"
              value={formData.deliveryDate}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank for immediate delivery</p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-300 font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-medium flex items-center space-x-2"
            >
              <Heart size={18} />
              <span>Send Gift</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftModal;
